import React from 'react';
import StatusBadge from '../../components/StatusBadge';
import { submitterLabel } from '../../utils/submitterName';
import { HEADER_CELL } from '../../utils/tableColumns';
import {
  distributorSaleAmount,
  distributorShippingAdjustment,
  distributorHoldLabel,
} from './DistributorOrderDetails';

const COLUMNS =
  'grid-cols-[minmax(100px,2.39fr)_minmax(116px,5.08fr)_minmax(116px,3.12fr)_minmax(88px,2.25fr)_minmax(116px,2.7fr)_minmax(24px,1.64fr)_minmax(84px,2.24fr)_minmax(76px,2.15fr)_112px]';
const AMOUNT_COLUMNS =
  'grid-cols-[minmax(100px,2.39fr)_minmax(116px,5.08fr)_minmax(116px,3.12fr)_minmax(88px,2.25fr)_minmax(116px,2.7fr)_minmax(24px,1.64fr)_minmax(84px,2.24fr)_minmax(76px,2.15fr)_minmax(112px,2.2fr)_112px]';

// Presentation only: paging, filters and actions remain owned by OrderManagement.
export default function OrderListTable({
  orders,
  admin,
  showSaleAmount,
  showDistributor = false,
  distributors = [],
  t,
  locale,
  onView,
  onDelete,
  showHold = admin,
  formatDate,
  dateLabel = 'DATE',
  statusForOrder,
}) {
  const columns = showDistributor
    ? 'grid-cols-[minmax(100px,2.39fr)_minmax(116px,5.08fr)_minmax(116px,3.12fr)_minmax(120px,3fr)_minmax(88px,2.25fr)_minmax(116px,2.7fr)_minmax(24px,1.64fr)_minmax(84px,2.24fr)_minmax(76px,2.15fr)_112px]'
    : showSaleAmount
      ? AMOUNT_COLUMNS
      : COLUMNS;
  return (
    <div
      role="table"
      aria-label={t('Orders')}
      className={showDistributor ? 'min-w-[1200px]' : undefined}
    >
      <div
        role="rowgroup"
        className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px]"
      >
        <div role="row" className={`grid ${columns} px-5 py-4 gap-2`}>
          {[
            'ORDER ID',
            'PRODUCT',
            'PRACTICE',
            ...(showDistributor ? ['Distributor'] : []),
            'CUSTOMER PO',
            'SUBMITTED BY',
            'QTY',
            'STATUS',
            dateLabel,
            ...(showSaleAmount ? ['Product subtotal'] : []),
            'ACTIONS',
          ].map((col) => (
            <span role="columnheader" key={col} className={HEADER_CELL}>
              {t(col)}
              {col === 'Product subtotal' ? ' (USD)' : ''}
            </span>
          ))}
        </div>
      </div>
      <div role="rowgroup" className="divide-y divide-[#e2e8f0]">
        {orders.length === 0 ? (
          <div role="row" className="px-5 py-8 text-center text-sm text-[#64748b]">
            <span role="cell">{t('No orders found')}</span>
          </div>
        ) : (
          orders.map((order) => {
            /**
             * SUBMITTED BY, not SALES REP: clinics and admins submit
             * orders too. This column read order.salesRep, which orders.service only sets when
             * the creator's role is sales_rep - so every clinic and admin order said
             * "Unassigned" under a heading that made that look like a rep had not been picked
             * yet. order.patient is the submitter and is set on EVERY order.
             *
             * The sales rep is still a real field, still shown in the order detail panel, and an
             * admin can still reassign it - it is simply a different question from who filed
             * this order, which is what the list is read for.
             */
            const submittedBy = submitterLabel(order.patient, t);
            // The clinic is the practice. This used to read
            // doctor.department, which dates from before orders carried a clinic at all - so
            // with the provider picker removed it would have gone blank on every
            // new order. The doctor fallback stays for legacy orders that predate the clinic.
            const practiceName = order.clinic?.name || order.doctor?.department || '—';
            const organization = distributors.find(
              (row) => row._id === (order.distributorId?._id || order.distributorId)
            );
            // No fallback to order.patient. This cell is the Customer PO; order.patient is the
            // USER who submitted the order, so the fallback printed a staff name under a PO
            // heading - and now prints it a second time, one column to the right. An order with
            // no PO shows an em dash, which is what an empty field looks like everywhere else.
            const customerPO = order.patientName || '—';
            const date = formatDate
              ? formatDate(order.createdAt)
              : order.createdAt
                ? new Date(order.createdAt).toLocaleDateString(locale, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                : '';
            return (
              <div
                key={order._id}
                role="row"
                className={`grid ${columns} px-5 py-4 gap-2 items-center hover:bg-gray-50/50 transition-colors`}
              >
                <span role="cell" className="text-xs font-medium text-[#0f172a] truncate">
                  {order.orderId}
                  {showHold && distributorHoldLabel(order) && (
                    <span
                      className="block text-[10px] text-amber-800"
                      title={t(distributorHoldLabel(order))}
                    >
                      {t(distributorHoldLabel(order))}
                    </span>
                  )}
                  {distributorShippingAdjustment(order) && (
                    <span
                      className="block text-[10px] text-blue-700"
                      title={t('Delivery date updated')}
                    >
                      {t('Delivery date updated')}
                    </span>
                  )}
                </span>
                <span
                  role="cell"
                  className="text-xs font-medium text-[#0f172a] truncate"
                  title={
                    order.lineItems && order.lineItems.length > 0
                      ? order.lineItems.map((li) => li.product?.name || t('N/A')).join(', ')
                      : order.product?.name || t('N/A')
                  }
                >
                  {order.lineItems && order.lineItems.length > 0
                    ? order.lineItems.map((li) => li.product?.name || t('N/A')).join(', ')
                    : order.product?.name || t('N/A')}
                </span>
                <span
                  role="cell"
                  className="text-xs font-medium text-[#0f172a] truncate"
                  title={practiceName}
                >
                  {practiceName}
                  {admin && !showDistributor && (
                    <span className="block text-[10px] text-[#64748b] truncate">
                      {order.businessContext === 'distributor'
                        ? `${t('Distributor')}${organization ? ` · ${organization.name}` : ''}`
                        : t('Direct MedEffects')}
                    </span>
                  )}
                </span>
                {showDistributor && (
                  <span
                    role="cell"
                    className="text-xs font-medium text-[#0f172a] truncate"
                    title={organization?.name || order.distributorId?.name || t('Unavailable')}
                  >
                    {organization?.name || order.distributorId?.name || t('Unavailable')}
                  </span>
                )}
                <span
                  role="cell"
                  className="text-xs font-medium text-[#0f172a] truncate"
                  title={customerPO}
                >
                  {customerPO}
                </span>
                <span
                  role="cell"
                  className="text-xs font-medium text-[#0f172a] truncate"
                  title={submittedBy}
                >
                  {submittedBy}
                </span>
                <span role="cell" className="text-xs font-medium text-[#0f172a]">
                  {order.lineItems && order.lineItems.length > 0
                    ? order.lineItems.reduce((sum, li) => sum + (li.quantity || 0), 0)
                    : order.quantity}
                </span>
                <span role="cell">
                  <StatusBadge
                    status={
                      statusForOrder
                        ? statusForOrder(order)
                        : order.businessContext === 'distributor' && order.status === 'submitted'
                          ? 'Order received \u2014 processing'
                          : order.status
                    }
                  />
                </span>
                <span role="cell" className="text-xs font-medium text-[#0f172a] truncate">
                  {date}
                </span>
                {showSaleAmount && (
                  <span role="cell" className="text-xs font-medium text-[#0f172a]">
                    {order.businessContext === 'distributor'
                      ? distributorSaleAmount(order, t)
                      : '—'}
                  </span>
                )}
                <span role="cell" className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onView(order)}
                    aria-label={t('View order details')}
                    className="inline-flex items-center gap-1.5 w-fit px-2.5 py-1 rounded-[6px] border border-[#e2e8f0] text-xs font-medium text-[#0f172a] hover:bg-gray-50 hover:border-[#cbd5e1] transition-colors"
                  >
                    <span className="material-symbols-outlined text-[16px] text-[#64748b]">
                      visibility
                    </span>
                    {t('View')}
                  </button>
                  {admin && order.businessContext !== 'distributor' && (
                    <button
                      type="button"
                      onClick={() => onDelete(order)}
                      aria-label={t('Delete order {id}', { id: order.orderId })}
                      title={t('Delete order')}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-[6px] border border-[#e2e8f0] text-[#64748b] hover:bg-[#fef2f2] hover:text-[#b91c1c] hover:border-[#fecaca] transition-colors"
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
